import { h } from 'preact'
import { useState, useEffect, useRef } from 'preact/compat'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import {
  pageStateUpdate,
  pageStateSelector,
  componentsLoadUpdate,
} from '../../../redux/slice/pageStateSlice'
import CardDebit from '../../../assets/icons/card-debit.svg'
import Group from '../../Common/Group'
import SelectCard from '../../Common/SelectCard'
import { loadApplePayScript } from '../../PaymentButtons/ApplePay/handler'
import { paymentMethodsMap, desktopSort, mobileSort } from './paymentMethodsMap'
import { useTranslations } from '../../../hooks/useTranslations'
import {
  formatAsCurrency,
  isMobile,
  isNumberBetweenTwoNumbers,
} from '../../../helpers/common'
import { DataDog } from '../../../datadog'
import { mergeBillingAddressOverride } from '../../../helpers/billingAddress'
import { PAYMENT_METHOD_TYPES } from '../../../constants/paymentMethods'
import {
  transactionDetailsSelector,
  transactionDetailsUpdate,
  hookBillingAddressOverrideUpdate,
} from '../../../redux/slice/transactionDetailsSlice'
import {
  dispatchMFEAdobeEvent,
  dispatchMFEEvent,
} from '../../../helpers/events'
import GooglePayAdapter from '../../PaymentButtons/GooglePay/GooglePayAdapter'
import './styles.scss'
import { LOCALES } from '../../../constants/locales'

const PaymentMethods = ({ onSelect = () => {} }) => {
  const l10n = useTranslations()
  const sortMap = isMobile() ? mobileSort : desktopSort
  const dispatch = useDispatch()
  const pageState = useSelector(pageStateSelector)

  const {
    availablePaymentMethods,
    allowedPaymentMethods,
    defaultPaymentMethod,
    tenantPaymentMethods,
    tenantSelectedPaymentMethod,
    hideBillingAddress,
    billingAddress,
    amount,
    overrideAmount,
    currency,
    culture,
    currentPaymentPlan,
    directDebit,
    googlePay,
    applePay,
    id,
    countryCode,
    prohibitedCardBrands,
    customPaymentMethods,
    initCulture,
  } = useSelector(transactionDetailsSelector)

  const formatAmount = formatAsCurrency(currency, culture)

  useEffect(() => {
    const firstStartDay = directDebit?.schedules[0]?.startDay
    const smallestStartDay = directDebit.schedules.reduce(
      (acc, item) => Math.min(acc, item.startDay),
      firstStartDay,
    )

    const currentSchedule = directDebit.schedules.find(({ startDay, endDay }) =>
      isNumberBetweenTwoNumbers(startDay, endDay, smallestStartDay),
    )

    if (!currentSchedule) return

    const planAmount = overrideAmount
      ? formatAmount(overrideAmount / currentSchedule.count)
      : formatAmount(currentSchedule.amount)

    dispatch(
      transactionDetailsUpdate({
        currentPaymentPlan: {
          count: currentSchedule.count,
          amount: planAmount,
        },
      }),
    )
  }, [directDebit.schedules.length])

  const [paymentMethods, setPaymentMethods] = useState([])
  const [currentPaymentMethods, setCurrentPaymentMethods] = useState([])
  const isApplePaySupported = useRef(null)
  const isGooglePayLoaded = useRef(null)

  const [isApplePayReady, setIsApplePayReady] = useState(false)
  const [isGooglePayReady, setIsGooglePayReady] = useState(false)

  const { isConfigLoaded } = pageState.componentsLoad

  useEffect(() => {
    const customPaymentMethodsNames = customPaymentMethods.map(pm => pm.id)

    // Diagnostics below may only run once the payment-method configuration for
    // the CURRENT transaction has actually arrived. isConfigLoaded is that
    // signal: the details saga sets it true only on a successful response (and
    // only after the allow-list is already in the store), while useToken sets it
    // back to false on every token change BEFORE clearing the details. It
    // therefore cannot carry over from the previous transaction during a live
    // re-initialisation (tenant swaps `token` + `available-payment-methods` on
    // the mounted element), which is the race these gates were added for.
    //
    // Do NOT gate on `allowedPaymentMethods.length > 0`: a non-empty allow-list
    // is not the same thing as a completed request. A successful response may
    // legitimately come back with an EMPTY allow-list, and that is exactly the
    // misconfiguration these diagnostics exist to report — nothing resolves, the
    // component never renders, and the user is stuck on the skeleton loader.
    // Using the allow-list as a proxy silently suppressed that alert.
    const isMethodConfigReady = isConfigLoaded

    const getAllowedTenantPaymentMetods = () => {
      const filteredOutMethods = []

      const allowed = tenantPaymentMethods.filter(method => {
        if (
          allowedPaymentMethods.includes(method) ||
          customPaymentMethodsNames.includes(method)
        ) {
          return true
        }
        filteredOutMethods.push(method)
        return false
      })

      if (isMethodConfigReady && filteredOutMethods.length) {
        DataDog.warn(
          'Tenant-provided payment methods (MFE attributes) filtered out: not present in allowedPaymentMethods from BE config',
          {
            filteredOutMethods,
            tenantPaymentMethods,
            allowedPaymentMethods,
          },
        )
        DataDog.info('Payment methods resolved after filtering', {
          resolvedMethods: allowed,
        })
      }

      return allowed
    }

    const methods = tenantPaymentMethods.length
      ? getAllowedTenantPaymentMetods()
      : availablePaymentMethods

    const resolvedMethods = [...methods, ...customPaymentMethodsNames]

    // Genuine "no methods" only once the config for this transaction has
    // actually arrived (successful details response). See the note above.
    if (isMethodConfigReady && !resolvedMethods.length) {
      DataDog.error(
        'No payment methods available after filtering — component will not render (potential infinite skeleton loader)',
        {
          tenantPaymentMethods,
          availablePaymentMethods,
          allowedPaymentMethods,
          customPaymentMethodsNames,
        },
      )
    }

    setCurrentPaymentMethods(resolvedMethods)
    // isConfigLoaded must be a dependency: when a successful response carries an
    // empty allow-list the `allowedPaymentMethods` reference may not change at
    // all (it stays the initial []), so without this the effect would never
    // re-run to evaluate the diagnostics for that transaction.
  }, [
    allowedPaymentMethods,
    tenantPaymentMethods,
    availablePaymentMethods,
    isConfigLoaded,
  ])

  const urlParams = new URLSearchParams(window.location.search)
  const hasIdealErrorFromUrl = urlParams.get('errorMessage')

  const defaultPaymentMethodValue = hasIdealErrorFromUrl
    ? PAYMENT_METHOD_TYPES.IDeal
    : tenantSelectedPaymentMethod || defaultPaymentMethod

  const [currentSelectedPaymentMethod, setCurrentSelectedPaymentMethod] =
    useState(defaultPaymentMethodValue)

  useEffect(() => {
    if (!LOCALES.includes(initCulture)) {
      DataDog.info(
        `Wrong locale ${initCulture} was used, instead of this default locale 'en-gb' added`,
      )
    }
  }, [])

  useEffect(() => {
    setCurrentSelectedPaymentMethod(tenantSelectedPaymentMethod)
  }, [tenantSelectedPaymentMethod])

  useEffect(() => {
    setCurrentSelectedPaymentMethod(defaultPaymentMethodValue)
  }, [defaultPaymentMethodValue])

  const handlePaymentMethods = (
    withApplePay = false,
    withGooglePay = false,
  ) => {
    const tmpMethodsObject = []
    currentPaymentMethods.forEach(Type => {
      if (paymentMethodsMap.has(Type)) {
        const method = paymentMethodsMap.get(Type)

        if (
          (!withApplePay && method.isIOSRequired) ||
          (!withGooglePay && method.isGooglePaySupportRequired)
        ) {
          return
        }

        if (
          method.isIOSRequired &&
          isApplePaySupported.current &&
          tenantPaymentMethods.includes(PAYMENT_METHOD_TYPES.ApplePay)
        ) {
          method.isChecked = true
        } else if (
          !tenantPaymentMethods.includes(PAYMENT_METHOD_TYPES.ApplePay) ||
          !isApplePaySupported.current
        ) {
          method.isChecked = Type === currentSelectedPaymentMethod
        } else {
          method.isChecked = false
        }
        tmpMethodsObject.push(method)
      }
    })

    if (customPaymentMethods?.length) {
      const defaultPaymentMethodValues = {
        name: undefined,
        title: undefined,
        logo: undefined,
        subLogo: () => {},
        isIOSRequired: false,
        isGooglePaySupportRequired: false,
        content: () => {},
        isChecked: false,
        className: 'custom-payment-method',
      }

      customPaymentMethods.map(customPaymentMethod =>
        tmpMethodsObject.push({
          ...defaultPaymentMethodValues,
          logo: customPaymentMethod.iconUrl,
          title: customPaymentMethod.id,
          name: customPaymentMethod.id,
        }),
      )
    }

    setPaymentMethods(tmpMethodsObject)

    dispatch(
      pageStateUpdate({
        displayedPaymentMethods: tmpMethodsObject.map(({ name }) => name),
      }),
    )
  }

  const checkApplePay = async () => {
    try {
      await loadApplePayScript()
    } catch (err) {
      DataDog.error('Apple Pay script failed to load', err)
      console.error('Apple Pay script failed to load', err)
      return false
    }

    const { ApplePaySession } = window

    if (ApplePaySession) {
      let canMakeApplePayment = false

      if (ApplePaySession.applePayCapabilities) {
        const capabilities = await ApplePaySession.applePayCapabilities(
          applePay?.merchantId,
        )

        // Check whether the person has an active payment credential provisioned in Wallet.
        switch (capabilities?.paymentCredentialStatus) {
          case 'paymentCredentialStatusUnknown':
          case 'paymentCredentialsAvailable':
            canMakeApplePayment = true
            // Display an Apple Pay button and offer Apple Pay as the primary payment option.
            break
          case 'paymentCredentialsUnavailable':
          case 'applePayUnsupported':
          default:
            canMakeApplePayment = false
            break
        }

        if (!canMakeApplePayment) {
          DataDog.info(
            `ApplePay credentials status: ${capabilities?.paymentCredentialStatus}. The user's device doesn't support making payments with Apple Pay, Apple Pay has been hidden`,
          )
        } else {
          DataDog.info(
            `ApplePaySession.applePayCapabilities is available, credentials status: ${capabilities?.paymentCredentialStatus}`,
          )
        }
        return canMakeApplePayment
      }

      canMakeApplePayment = ApplePaySession.canMakePayments()

      if (!canMakeApplePayment) {
        DataDog.info(
          `The user's device doesn't support making payments with Apple Pay, Apple Pay has been hidden`,
        )
      }

      return canMakeApplePayment
      // return true // use it for localhost env
    }

    DataDog.info(
      'No ApplePaySession entry point on the page, Apple Pay has been hidden',
    )
    return false
  }

  const checkGooglePay = async () => {
    if (GooglePayAdapter.isInitialised) {
      const isGooglePaySupported = await GooglePayAdapter.isReadyToPay()

      if (!isGooglePaySupported) {
        DataDog.info(
          "The user's browser doesn't support Google Pay, Google Pay has been hidden",
        )
      }

      return isGooglePaySupported
    }

    const totalPrice = ((overrideAmount || amount) / 100).toFixed(2)

    GooglePayAdapter.updatePaymentConfig({
      ...googlePay,
      currencyCode: currency,
      transactionId: id,
      countryCode,
      totalPrice,
    })

    GooglePayAdapter.setProhibitedCardNetworks(prohibitedCardBrands)

    try {
      await GooglePayAdapter.loadGooglePayScript()
    } catch (err) {
      DataDog.error('Google Pay script failed to load', err)
      return false
    }

    DataDog.info('Google Pay scripts has been initialised')

    const isGooglePaySupported = await GooglePayAdapter.isReadyToPay()

    if (!isGooglePaySupported) {
      DataDog.info(
        "The user's browser doesn't support Google Pay, Google Pay has been hidden",
      )
    }

    return isGooglePaySupported
  }

  useEffect(() => {
    // Wait until Apple Pay is ready
    if (isApplePaySupported.current !== null) {
      setIsApplePayReady(true)
    }

    if (currentSelectedPaymentMethod) {
      dispatchMFEEvent('tui-methods.selected', currentSelectedPaymentMethod)
    }
  }, [currentSelectedPaymentMethod])

  useEffect(() => {
    if (
      currentPaymentMethods.includes(PAYMENT_METHOD_TYPES.ApplePay) &&
      !isApplePaySupported.current
    ) {
      isApplePaySupported.current = null
    }
  }, [currentPaymentMethods])

  useEffect(async () => {
    if (currentPaymentMethods.length) {
      if (isApplePaySupported.current === null) {
        isApplePaySupported.current = false
        isApplePaySupported.current =
          currentPaymentMethods.includes(PAYMENT_METHOD_TYPES.ApplePay) &&
          (await checkApplePay())

        if (
          isApplePaySupported.current &&
          tenantPaymentMethods.includes(PAYMENT_METHOD_TYPES.ApplePay)
        ) {
          if (!currentSelectedPaymentMethod) {
            setCurrentSelectedPaymentMethod(PAYMENT_METHOD_TYPES.ApplePay)
          } else {
            setIsApplePayReady(true)
          }
        } else {
          setIsApplePayReady(true)
        }
      }

      if (isGooglePayLoaded.current === null) {
        isGooglePayLoaded.current = false
        isGooglePayLoaded.current =
          currentPaymentMethods.includes(PAYMENT_METHOD_TYPES.GooglePay) &&
          (await checkGooglePay())

        setIsGooglePayReady(true)
      }

      handlePaymentMethods(
        isApplePaySupported.current,
        isGooglePayLoaded.current,
      )
    }
  }, [
    currentPaymentMethods,
    currentSelectedPaymentMethod,
    tenantPaymentMethods,
    tenantSelectedPaymentMethod,
  ])

  const applyBillingAddressOverride = overrideValues => {
    const mergedBillingAddress = mergeBillingAddressOverride(
      billingAddress,
      overrideValues,
    )

    if (mergedBillingAddress) {
      dispatch(
        transactionDetailsUpdate({ billingAddress: mergedBillingAddress }),
      )
      // Persist the override so it survives later card-form writes and is
      // re-applied (override wins) at authorization time.
      dispatch(hookBillingAddressOverrideUpdate(overrideValues.billingAddress))
    }
  }

  const onClick = async event => {
    const { value } = event.target
    const mfeElement = window.tuiPaymentsMFEInstance
    const changePaymentMethod = () => {
      const updatedPaymentMethods = paymentMethods.map(pm => {
        pm.isChecked = pm.name === value
        return pm
      })

      onSelect(value)

      setPaymentMethods(updatedPaymentMethods)

      const overrideValues = mfeElement?.onMethodSelected?.({
        paymentMethod: value,
        billingAddress: billingAddress || {},
      })
      applyBillingAddressOverride(overrideValues)

      dispatchMFEAdobeEvent(`Click-${value}`)
      DataDog.info(`Changed payment method to ${value}`)
    }

    if (mfeElement?.onMethodSelecting) {
      event.preventDefault()
      DataDog.info(`onMethodSelecting hook has been enabled`)

      mfeElement
        .onMethodSelecting?.({
          paymentMethod: value,
          billingAddress: billingAddress || {},
        })
        .then(overrideValues => {
          setTimeout(() => {
            event.target.checked = true
            applyBillingAddressOverride(overrideValues)
            changePaymentMethod()
            DataDog.info(`onMethodSelecting Promise resolved`)
          }, 1)
        })
        .catch(() => {
          DataDog.info(`onMethodSelecting Promise rejected`)
          console.log('onMethodSelecting Promise rejected')
        })
    } else {
      changePaymentMethod()
    }
  }

  useEffect(() => {
    const isPaymentMethodsReady = !(
      !currentPaymentMethods?.length ||
      !pageState.displayedPaymentMethods ||
      !paymentMethods?.length ||
      (currentPaymentMethods.includes(PAYMENT_METHOD_TYPES.ApplePay) &&
        !isApplePayReady) ||
      (currentPaymentMethods.includes(PAYMENT_METHOD_TYPES.GooglePay) &&
        !isGooglePayReady)
    )

    dispatch(
      componentsLoadUpdate({
        isPaymentMethodsReady,
      }),
    )
  }, [
    currentPaymentMethods.length,
    pageState.displayedPaymentMethods,
    paymentMethods,
    isApplePayReady,
    isGooglePayReady,
  ])

  const getPaymentMethodContent = paymentMethod => {
    let currentMethod = paymentMethods.find(
      method => method.name === paymentMethod,
    )
    const isCustomPaymentMethod = customPaymentMethods.some(
      pm => pm.id === paymentMethod.name,
    )

    if (!currentMethod && !isCustomPaymentMethod) {
      return null
    }

    if (isCustomPaymentMethod) {
      currentMethod = paymentMethod
    }

    const totalPrice = formatAmount(overrideAmount || amount)

    const {
      logo,
      subLogo,
      name,
      content,
      isChecked,
      title,
      subTitle,
      className,
      showTotalPrice,
    } = currentMethod

    return (
      <SelectCard
        key={name}
        inputName="paymentMethods"
        name={name}
        value={name}
        onClick={onClick}
        isChecked={isChecked}
        title={l10n.t(title)}
        logo={logo}
        subLogo={subLogo}
        subTitle={subTitle ? l10n.t(subTitle) : ''}
        totalPrice={totalPrice}
        showTotalPrice={showTotalPrice}
        className={className}
      >
        {content(l10n, { currentPaymentPlan, showTotalPrice, totalPrice })}
      </SelectCard>
    )
  }

  let paymentMethodsToShow = tenantPaymentMethods.length
    ? tenantPaymentMethods
    : sortMap

  if (hideBillingAddress) {
    paymentMethodsToShow = paymentMethodsToShow.filter(
      el => el !== PAYMENT_METHOD_TYPES.DirectDebit,
    )
  }

  if (
    customPaymentMethods?.length &&
    !paymentMethodsToShow.some(item => item === customPaymentMethods[0].id)
  ) {
    customPaymentMethods.forEach(customPaymentMethod => {
      paymentMethodsToShow = [...paymentMethodsToShow, customPaymentMethod.id]
    })
  }

  return (
    Boolean(paymentMethods.length) && (
      <tui-payments-methods className="mfe-component">
        <Group
          type="payment"
          title={l10n.t('Payment method')}
          icon={CardDebit}
          paymentMethods={paymentMethods}
          withDelimiter={false}
          withBorder={false}
        >
          <div className="payment-methods-wrapper">
            <p className="payment-methods__header text lead large">
              {l10n.t('How would you like to pay?')}
            </p>

            <div className="payment-methods small-view">
              {paymentMethodsToShow.map(paymentMethod =>
                getPaymentMethodContent(paymentMethod),
              )}
            </div>
          </div>
        </Group>
      </tui-payments-methods>
    )
  )
}

PaymentMethods.propTypes = {
  onSelect: PropTypes.func,
}

export default PaymentMethods
